import {
	DATE_AND_TIME,
	FixtureTypes,
	MillisecondsPer,
	TimeZoneFormat,
} from '@shared/utils/constants';
import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import HIIHirePeriod from '@shared/hireInvoice/HIIHirePeriod';
import HIIOffHirePeriod from '@shared/hireInvoice/HIIOffHirePeriod';
import HIIBallastBonus from '@shared/hireInvoice/HIIBallastBonus';
import {
	calculateTotal,
	convertCurrency,
	round,
} from '@shared/utils/math';
import HIIFreight from '@shared/hireInvoice/HIIFreight';
import HIIOverage from '@shared/hireInvoice/HIIOverage';
import HIIDeadfreight from '@shared/hireInvoice/HIIDeadfreight';
import HIIRevenueItem from '@shared/hireInvoice/HIIRevenueItem';
import HIIDemurrage from '@shared/hireInvoice/HIIDemurrage';
import HIIDespatch from '@shared/hireInvoice/HIIDespatch';
import HIIBbItem from '@shared/hireInvoice/HIIBbItem';
import normalize from '@shared/utils/normalizeNumber';
import {
	formatDate,
	toMoment,
} from '@shared/utils/date';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import type { SpotFixtureProps } from '@api/models/spot-fixture';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { VoyageInvoice } from '@api/utils/sequelize/getAllVoyageInvoices';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';

export type BrokerInvoiceItem = {
	itemId: number;
	brokerId: number;
	brokerName: string;
	invoiceId: number | null;
	commissionPercent: number | null;
	commissionAmount: number;
	invoiceAmount: number;
	invoiceIdentifier: string;
	type: string;
	freightType: string;
	note: string;
	isTotal?: boolean;
	amount?: number;
};

type AmendmentEntry = {
	item: HireInvoiceItem;
};

/** Follow amendment chains (amend → amend again) to the latest replacement item. */
export const resolveLatestAmendment = (
	itemId: number,
	amendmentMap: Map<number, AmendmentEntry>,
): AmendmentEntry | undefined => {
	let current: AmendmentEntry | undefined;
	let currentItemId = itemId;
	const seen = new Set<number>();

	while (!seen.has(currentItemId)) {
		seen.add(currentItemId);

		const next = amendmentMap.get(currentItemId);

		if (next == null) {
			break;
		}

		current = next;
		currentItemId = next.item.id;
	}

	return current;
};

/**
 * Returns every amendment-item id along the chain starting from `itemId`
 * (intermediate amendments plus the final leaf). Empty when there is no chain.
 * Used to skip superseded amendment rows that still exist in the data set but should
 * not be rendered because a newer amendment of them exists.
 */
export const getAmendmentChainItemIds = (
	itemId: number,
	amendmentMap: Map<number, AmendmentEntry>,
): number[] => {
	const ids: number[] = [];
	let currentItemId = itemId;
	const seen = new Set<number>();

	while (!seen.has(currentItemId)) {
		seen.add(currentItemId);

		const next = amendmentMap.get(currentItemId);

		if (next == null) {
			break;
		}

		ids.push(next.item.id);
		currentItemId = next.item.id;
	}

	return ids;
};

const { UTC } = TimeZoneFormat;
export const getBrokerInvoiceMap = ({
	fixtureDetails,
	voyageDetails,
	hireInvoices,
}: {
	fixtureDetails: GetFixtureDetailsResponse<SpotFixtureProps | TcFixtureProps | BbFixtureProps>;
	voyageDetails: GetVoyageDetailsResponse;
	hireInvoices: VoyageInvoice[] | undefined;
}) => {
	const hireInvoicesByItemId: { [s: string]: { invoiceIdentifier: string } } =
		(hireInvoices ?? []).reduce((obj, hireInvoice) => ({
			...obj,
			...hireInvoice.items
				.filter((i) => !i.isPrevious)
				.reduce((itemObj, i) => ({
					...itemObj,
					[i.id]: hireInvoice,
				}), {}),
		}), {});

	const getInvoiceIdentifier = (item: HireInvoiceItem) => (
		hireInvoicesByItemId[item.id as keyof typeof hireInvoicesByItemId]?.invoiceIdentifier ||
	'Cumulative'
	);

	const allItems = [
		...(hireInvoices ?? []).reduce<Array<HireInvoiceItem>>((arr, hireInvoice) => [
			...arr,
			...hireInvoice.items,
		], []).filter((i) => !i.isPrevious),
		...voyageDetails.cumulativeItems.map((i) => HireInvoiceItem.fromJSON(i)),
	];

	const amendmentMap = (() => {
		const map = new Map<number, AmendmentEntry>();

		const amendments = (voyageDetails as any)?.amendments ?? [];

		const newestFirst = [...amendments].sort((a: any, b: any) => (
			new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf() ||
			Number(b.id ?? 0) - Number(a.id ?? 0)
		));

		newestFirst.forEach((a: any) => {
			const originalId = a?.originalItemId ?? a?.originalItem?.id;

			if (originalId == null || map.has(Number(originalId))) {
				return;
			}

			const amendmentItem = a?.amendmentItem;

			if (amendmentItem == null) {
				return;
			}

			map.set(Number(originalId), {
				item: amendmentItem instanceof HireInvoiceItem ?
					amendmentItem :
					HireInvoiceItem.fromJSON(amendmentItem),
			});
		});

		return map;
	})();

	// Latest-only shaping:
	// - never show reversals
	// - show originals as their latest amendment when available
	// - hide amendment rows that are superseded by a newer amendment
	const effectiveItems = (() => {
		if (amendmentMap.size === 0) {
			return allItems.filter((i) => !i.isReversal);
		}

		const referencedAmendmentIds = new Set<number>();
		allItems.forEach((item) => {
			if (item.isReversal || item.isAmendment) {
				return;
			}

			getAmendmentChainItemIds(item.id, amendmentMap).forEach((id) => {
				referencedAmendmentIds.add(id);
			});
		});

		const shaped = allItems
			.filter((i) => !i.isReversal)
			.filter((i) => !(i.isAmendment && referencedAmendmentIds.has(i.id)))
			.map((item) => (item.isAmendment ? item : (resolveLatestAmendment(item.id, amendmentMap)?.item ?? item)))
			.map((item) => {
				if (!(item instanceof HIIHirePeriod)) {
					return item;
				}

				const candidateChildren = item.children;
				const referencedAmendmentChildIds = new Set<number>();
				candidateChildren.forEach((child) => {
					if (child.isReversal || child.isAmendment) {
						return;
					}

					getAmendmentChainItemIds(child.id, amendmentMap).forEach((id) => {
						referencedAmendmentChildIds.add(id);
					});
				});

				const nextItem = item.copy() as HIIHirePeriod;
				nextItem.children = candidateChildren
					.filter((c) => !c.isReversal)
					.filter((c) => !(c.isAmendment && referencedAmendmentChildIds.has(c.id)))
					.map((c) => (c.isAmendment ? c : (resolveLatestAmendment(c.id, amendmentMap)?.item ?? c)));

				return nextItem;
			});

		return shaped;
	})();

	const bbItemBrokerCommissions =
		effectiveItems
			.filter((i) => i instanceof HIIBbItem)
			.map((i) => ({
				item: i,
				total: i.getTotal(effectiveItems, false),
				invoiceIdentifier: getInvoiceIdentifier(i),
			}));

	const getSpotItems = (cargoId: number) => {
		const freightItems =
			effectiveItems
				.filter((i) => i instanceof HIIFreight && i.cargoId === cargoId)
				.map((i) => ({
					item: i,
					total: i.getTotal(effectiveItems, false),
					invoiceIdentifier: getInvoiceIdentifier(i),
				}));

		const overageItems =
			effectiveItems
				.filter((i) => i instanceof HIIOverage && i.cargoId === cargoId)
				.map((i) => ({
					item: i,
					total: i.getTotal(effectiveItems, false),
					invoiceIdentifier: getInvoiceIdentifier(i),
				}));

		const deadfreightItems =
			effectiveItems
				.filter((i) => i instanceof HIIDeadfreight && i.cargoId === cargoId)
				.map((i) => ({
					item: i,
					total: i.getTotal(effectiveItems, false),
					invoiceIdentifier: getInvoiceIdentifier(i),
				}));

		const demurrageItems =
			effectiveItems
				.filter((i) => i instanceof HIIDemurrage && i.cargoId === cargoId)
				.map((i) => ({
					item: i,
					total: i.getTotal(effectiveItems, false),
					invoiceIdentifier: getInvoiceIdentifier(i),
				}));

		const despatchItems =
			effectiveItems
				.filter((i) => i instanceof HIIDespatch && i.cargoId === cargoId)
				.map((i) => ({
					item: i,
					total: i.getTotal(effectiveItems, false),
					invoiceIdentifier: getInvoiceIdentifier(i),
				}));

		const revenueItems =
			effectiveItems
				.filter((i) => (
					i instanceof HIIRevenueItem &&
				i.cargoId === cargoId &&
				i.subjectToCommissions
				))
				.map((i) => ({
					item: i,
					total: i.getTotal(effectiveItems, false),
					invoiceIdentifier: getInvoiceIdentifier(i),
				}));

		return {
			freightItems,
			overageItems,
			deadfreightItems,
			demurrageItems,
			despatchItems,
			revenueItems,
		};
	};

	if (fixtureDetails.type === FixtureTypes.SPOT) {
		const spotBrokerItems: Record<string, BrokerInvoiceItem[]> = {};
		fixtureDetails.cargos.forEach((cargo) => {
			cargo.Brokers.forEach((cb) => {
				const notesByHireInvoiceItemId = voyageDetails.brokerPayments
					.filter((b) => b.brokerId === cb.id)
					.reduce((obj, brokerPayment) => ({
						...obj,
						[brokerPayment.hireInvoiceItemId]: brokerPayment.note,
					}), {});

				const getItemRows = (
					item: HireInvoiceItem,
					total: number,
					invoiceIdentifier: string,
					type: string,
				): BrokerInvoiceItem => {
					const toFixtureCurrency = (amount: number) => (
						cargo.currency === item.fixtureCurrency ?
							amount :
							convertCurrency(amount, cargo.exchangeRate ?? 1)
					);

					return {
						itemId: item.id,
						brokerId: cb.id,
						brokerName: cb.name,
						invoiceId: item.hireInvoiceId,
						commissionPercent: cb.BrokerInCargo.commission,
						commissionAmount: toFixtureCurrency((total / 100) * (cb.BrokerInCargo.commission ?? 0)),
						invoiceAmount: toFixtureCurrency(total),
						invoiceIdentifier,
						type: cargo.type,
						freightType: type,
						note: notesByHireInvoiceItemId[item.id as keyof typeof notesByHireInvoiceItemId],
					};
				};

				const {
					freightItems,
					overageItems,
					deadfreightItems,
					despatchItems,
					demurrageItems,
					revenueItems,
				} = getSpotItems(cargo.id);

				const fItems = freightItems
					.map(({ 
						item, 
						total, 
						invoiceIdentifier 
					}) => getItemRows(item, total, invoiceIdentifier, 'Freight'));

				const rows = [
					...fItems,
					...overageItems.map(({ item, total, invoiceIdentifier }) => getItemRows(item, total, invoiceIdentifier, 'Overage')),
					...deadfreightItems
						.map(({ item, total, invoiceIdentifier }) => getItemRows(item, total, invoiceIdentifier, 'Deadfreight')),
					...despatchItems.map(({ item, total, invoiceIdentifier }) => getItemRows(item, total, invoiceIdentifier, 'Despatch')),
					...demurrageItems.map(({ item, total, invoiceIdentifier }) => getItemRows(item, total, invoiceIdentifier, 'Demurrage')),
					...revenueItems.map(({ item, total, invoiceIdentifier }) => getItemRows(item, total, invoiceIdentifier, 'Revenue')),
					...bbItemBrokerCommissions
						.map(({ item, total, invoiceIdentifier }) => getItemRows(item, total, invoiceIdentifier, 'Commission')),

				];

				spotBrokerItems[cb.name] = [
					...(spotBrokerItems?.[cb.name] ?? []),
					...rows,
				];
			});
		});

		return spotBrokerItems;
	}

	const hirePeriodEntries =
		effectiveItems
			.filter((i) => i instanceof HIIHirePeriod)
			.map((i) => ({
				item: i,
				total: i.getTotal(effectiveItems, false),
				invoiceIdentifier: getInvoiceIdentifier(i),
			}));

	const offHireEntries =
		effectiveItems
			.filter((i) => i instanceof HIIOffHirePeriod)
			.map((i) => ({
				item: i,
				total: i.getTotal(effectiveItems, false),
				invoiceIdentifier: getInvoiceIdentifier(i),
			}));

	const getBallastBonusEntry = () => {
		const ballastBonus = effectiveItems.find((i) => i instanceof HIIBallastBonus);

		if (ballastBonus == null) {
			return undefined;
		}

		return {
			id: ballastBonus.id,
			item: ballastBonus,
			total: ballastBonus.amount,
			invoiceIdentifier: getInvoiceIdentifier(ballastBonus),
		};
	};

	const ballastBonusEntry = getBallastBonusEntry();

	const getRows = (broker: TcTermsWithIncludes['Brokers'][number]): BrokerInvoiceItem[] => {
		const notesByHireInvoiceItemId = voyageDetails.brokerPayments
			.filter((b) => b.brokerId === broker.id)
			.reduce((obj, brokerPayment) => ({
				...obj,
				[brokerPayment.hireInvoiceItemId]: brokerPayment.note,
			}), {});

		const rows = [
			...bbItemBrokerCommissions.map(({ item, total, invoiceIdentifier }) => ({
				itemId: item.id,
				brokerId: broker.id,
				brokerName: broker.name,
				invoiceId: item.hireInvoiceId,
				commissionPercent: broker.BrokerInFixture.commission,
				commissionAmount: total * (normalize(broker.BrokerInFixture.commission) / 100),
				invoiceAmount: total,
				invoiceIdentifier,
				type: 'BB Commission',
				freightType: 'BB Commission',
				note: notesByHireInvoiceItemId[item.id as keyof typeof notesByHireInvoiceItemId] ?? '',
				period: `${formatDate(item.from, DATE_AND_TIME, true, UTC)} - ${formatDate(item.to, DATE_AND_TIME, true, UTC)}`,
				days: round(toMoment(item.to).diff(item.from) / MillisecondsPer.DAY, 4),
				commission: broker.BrokerInFixture.commission,
				amount: total * ((broker.BrokerInFixture.commission ?? 0) / 100),
			})),
			...hirePeriodEntries.map(({ item, total, invoiceIdentifier }) => ({
				itemId: item.id,
				brokerId: broker.id,
				brokerName: broker.name,
				invoiceId: item.hireInvoiceId,
				commissionPercent: broker.BrokerInFixture.commission,
				commissionAmount: total * (normalize(broker.BrokerInFixture.commission) / 100),
				invoiceAmount: total,
				invoiceIdentifier,
				type: 'Hire Period',
				freightType: 'Hire Period',
				hireRate: item.hireRate,
				period: `${formatDate(item.from, DATE_AND_TIME, true, UTC)} - ${formatDate(item.to, DATE_AND_TIME, true, UTC)}`,
				days: round(toMoment(item.to).diff(item.from) / MillisecondsPer.DAY, 4),
				commission: broker.BrokerInFixture.commission,
				amount: total * ((broker.BrokerInFixture.commission ?? 0) / 100),
				note: notesByHireInvoiceItemId[item.id as keyof typeof notesByHireInvoiceItemId] ?? '',
			})),
			...offHireEntries.map(({ item, total, invoiceIdentifier }) => ({
				itemId: item.id,
				brokerId: broker.id,
				brokerName: broker.name,
				invoiceId: item.hireInvoiceId,
				commissionPercent: broker.BrokerInFixture.commission,
				commissionAmount: total * (normalize(broker.BrokerInFixture.commission) / 100),
				invoiceAmount: total,
				invoiceIdentifier,
				type: 'Off Hire',
				freightType: 'Off Hire',
				hireRate: item.hirePerDay,
				period: `${formatDate(item.startTime, DATE_AND_TIME, true, UTC)} - ${formatDate(item.endTime, DATE_AND_TIME, true, UTC)}`,
				days: round(toMoment(item.endTime).diff(item.startTime) / MillisecondsPer.DAY, 4),
				commission: broker.BrokerInFixture.commission,
				amount: total * ((broker.BrokerInFixture.commission ?? 0) / 100),
				note: notesByHireInvoiceItemId[item.id as keyof typeof notesByHireInvoiceItemId] ?? '',
			})),
			...(ballastBonusEntry != null ? [{
				itemId: ballastBonusEntry?.item?.id ?? -1,
				brokerId: broker.id,
				brokerName: broker.name,
				invoiceId: ballastBonusEntry?.item?.hireInvoiceId ?? null,
				commissionPercent: broker.BrokerInFixture.commission,
				commissionAmount: ballastBonusEntry?.total * (normalize(broker.BrokerInFixture.commission) / 100),
				invoiceAmount: ballastBonusEntry?.total,
				invoiceIdentifier: ballastBonusEntry?.invoiceIdentifier,
				type: 'Ballast Bonus',
				freightType: 'Ballast Bonus',
				note: notesByHireInvoiceItemId[
					ballastBonusEntry?.id as keyof typeof notesByHireInvoiceItemId
				] ?? '',
			}] : []),
		];

		return [
			...rows,
			{
				itemId: -1,
				brokerId: broker.id,
				brokerName: broker.name,
				invoiceId: null,
				commissionPercent: null,
				commissionAmount: calculateTotal(rows, (i) => i.commissionAmount),
				invoiceAmount: calculateTotal(rows, (i) => i.invoiceAmount),
				invoiceIdentifier: '',
				type: 'Total',
				freightType: 'Total',
				note: '',
				isTotal: true,
			},
		];
	};

	const result = voyageDetails.tcTerms.Brokers
		.reduce<Record<string, BrokerInvoiceItem[]>>((acc, broker) => {
			return {
				...acc,
				[broker.name]: getRows(broker),
			};
		}, {});

	return result;
};
